import { Skeleton, Text } from '@mantine/core';
import { IconAlertTriangle, IconInbox } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import classes from './ReadmodelWidgetStates.module.css';

/**
 * TV1-2291 (t25) — the shared NON-READY renderings for the four readmodel tiles.
 *
 * `readmodelState.ts` decides WHICH state a tile is in; this module draws the three
 * that are not "here is the data". They live together, once, because four tiles
 * drawing "nothing matched" four slightly different ways is how a report starts
 * looking like four different products.
 *
 * ⚠️ **Never a bare `<Skeleton>` for an empty collection.** That is the regression
 * `WidgetDataState` was introduced to kill on the Cube path (a failed query and a
 * pending one rendered identically, forever) and it is just as available here: an
 * empty readmodel is a real, final answer and must look like one. The skeleton
 * below is reachable ONLY from `kind: 'loading'`, i.e. no payload at all.
 *
 * Every colour is a `light-dark()` token in the CSS module — no inline colour, no
 * hex (`.claude/rules/design-system.md`).
 */

export type ReadmodelStateBlockProps = {
  icon: ReactNode;
  title: string;
  description?: string;
  testId: string;
  /** Extra class for the icon circle — the `unavailable` warning palette. */
  iconClassName?: string;
};

const ReadmodelStateBlock = ({ icon, title, description, testId, iconClassName }: ReadmodelStateBlockProps ) => (
  <div className={classes.state} data-testid={testId}>
    <div className={[ classes.stateIcon, iconClassName ].filter( Boolean ).join( ' ' )}>
      {icon}
    </div>
    <Text component="div" className={classes.stateTitle}>{title}</Text>
    {description ? (
      <Text component="div" className={classes.stateDescription}>{description}</Text>
    ) : null}
  </div>
);

/**
 * The state block, for a tile that is NOT readmodel-backed (2026-08-18).
 *
 * The two Cube tables (`table`, `criticalStock`) each rendered their empty state as
 * a bare `<Text size="sm" c="dimmed" p="md">` — a sentence stuck to the top-left
 * corner of a card, which is a direct breach of `design-system.md` §"Empty States"
 * ("Every view, list, tab, dropdown, and content area MUST have an empty state",
 * pattern 2: icon in a circle, title, description). The ticket owner called it out
 * by name: *"stock critico has horrible empty state"*.
 *
 * Exported as its own name rather than reusing `ReadmodelEmpty` because the COPY is
 * the part that differs — a Cube query that returns no rows is not "no row met the
 * readmodel condition" — while the TREATMENT must be identical, since a Cube table
 * and a readmodel list sit in the same report and two empty states drawn to two
 * scales read as two different products.
 */
export const WidgetEmptyState = ({ icon, title, description, testId }: ReadmodelStateBlockProps ) => (
  <ReadmodelStateBlock icon={icon} title={title} description={description} testId={testId} />
);

/** `kind: 'loading'` — no payload yet. The ONLY state that may render a skeleton. */
export const ReadmodelLoading = () => <Skeleton height="100%" />;

/**
 * `kind: 'empty'` as a BLOCK — for the tiles that have nothing to draw without rows
 * (`list`, `progress`). The two number tiles use {@link ReadmodelEmptyHint} instead:
 * they still render their measured `0`.
 *
 * The copy differentiates "nothing exists" from "your filter removed everything" the
 * only way this layer honestly can — it says the query ran and matched nothing, and
 * names the filter as the thing to check. The renderer cannot know whether the
 * collection is empty account-wide without a second, unfiltered query.
 */
export const ReadmodelEmpty = ({ title, description }: { title?: string; description?: string }) => {
  const { t } = useTranslation( 'widgets' );

  return (
    <ReadmodelStateBlock
      icon={<IconInbox size={20} />}
      title={title ?? t( 'widget_readmodel_empty_title', 'Nothing matches' )}
      description={description ?? t(
        'widget_readmodel_empty_description',
        'The query ran and no row met the condition. Check the card filter.'
      )}
      testId={componentsTestIds.readmodelWidgetEmpty}
    />
  );
};

/**
 * `kind: 'unavailable'` — a payload arrived that is not `{ rows, count }`.
 *
 * A DEFECT, and it says so rather than printing the `0` it could trivially have
 * fabricated. See `readmodelState.ts` for how a tile reaches this state at all.
 */
export const ReadmodelUnavailable = () => {
  const { t } = useTranslation( 'widgets' );

  return (
    <ReadmodelStateBlock
      icon={<IconAlertTriangle size={20} />}
      iconClassName={classes.unavailableIcon}
      title={t( 'widget_readmodel_unavailable_title', 'Data could not be read' )}
      description={t(
        'widget_readmodel_unavailable_description',
        'This card expects rows from an operational collection. Re-check its data source.'
      )}
      testId={componentsTestIds.readmodelWidgetUnavailable}
    />
  );
};

/**
 * `kind: 'empty'` as an INLINE hint — for `count` and `alert`, which keep rendering
 * the measured `0` beside it.
 *
 * A bare `0` cannot tell a reader whether the predicate matched nothing or the
 * widget is broken; this line is what makes the zero legible as an answer. It is
 * deliberately NOT the em-dash treatment: a readmodel count of zero IS a
 * measurement (see `readmodelState.ts`).
 */
export const ReadmodelEmptyHint = () => {
  const { t } = useTranslation( 'widgets' );

  return (
    <Text component="div" className={classes.emptyHint} data-testid={componentsTestIds.readmodelWidgetEmptyHint}>
      {t( 'widget_readmodel_empty_hint', 'Nothing matches right now' )}
    </Text>
  );
};
